export function normalizeProviderIconKey(providerKey: string | null | undefined): string {
  return String(providerKey || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
}

export type ProviderIconMode = 'color' | 'mono' | 'image'
export type ThemeName = 'light' | 'dark'

export interface ProviderIcon {
  /** Public path to the icon asset */
  src: string
  /**
   * - `color`: SVG already contains brand colors. Render natively via <img>.
   * - `mono`:  SVG uses `currentColor`. Render via CSS mask so it can be tinted
   *            with `tint` (brand color) or the inherited theme color.
   * - `image`: Raster (e.g. PNG). Render natively via <img>.
   */
  mode: ProviderIconMode
  /** Tint color for `mono` icons. If omitted, the icon follows currentColor. */
  tint?: string
}

interface ProviderIconDef {
  src: string | { light: string; dark: string }
  mode: ProviderIconMode
  tint?: string
}

const ICON_BASE = '/img/ai-provider-icons'

const PROVIDER_ICONS: Record<string, ProviderIconDef> = {
  openai: { src: `${ICON_BASE}/openai/svg/openai.svg`, mode: 'mono' },
  anthropic: { src: `${ICON_BASE}/anthropic/svg/anthropic.svg`, mode: 'mono', tint: '#D97757' },
  google: { src: `${ICON_BASE}/google-gemini/svg/gemini-color.svg`, mode: 'color' },
  groq: { src: `${ICON_BASE}/groq/svg/groq.svg`, mode: 'mono', tint: '#F55036' },
  grok: { src: `${ICON_BASE}/grok/svg/grok.svg`, mode: 'mono' },
  elevenlabs: { src: `${ICON_BASE}/elevenlabs/svg/elevenlabs.svg`, mode: 'mono' },
  cohere: { src: `${ICON_BASE}/cohere/svg/cohere-color.svg`, mode: 'color' },
  mistral: { src: `${ICON_BASE}/mistral/svg/mistral-color.svg`, mode: 'color' },
  copilot: { src: `${ICON_BASE}/microsoft-copilot/svg/copilot-color.svg`, mode: 'color' },
  'microsoft-copilot': { src: `${ICON_BASE}/microsoft-copilot/svg/copilot-color.svg`, mode: 'color' },
  openrouter: { src: `${ICON_BASE}/openrouter/svg/openrouter.svg`, mode: 'mono' },
  missionsquad: {
    src: {
      light: '/img/icons/dark-transparent.png',
      dark: '/img/icons/yellow-transparent.png'
    },
    mode: 'image'
  }
}

const PROVIDER_BRAND_COLORS: Record<string, string> = {
  openai: '#000000',
  anthropic: '#D97757',
  google: '#ffffff',
  groq: '#F55036',
  elevenlabs: '#ffffff',
  cohere: '#39594D',
  mistral: '#FA520F',
  grok: '#000000',
  copilot: '#0078D4',
  'microsoft-copilot': '#0078D4',
  openrouter: '#6566F1',
  missionsquad: '#35352d'
}

function resolveIconSrc(def: ProviderIconDef, theme: ThemeName): string {
  return typeof def.src === 'string' ? def.src : def.src[theme]
}

export function getProviderIcon(
  providerKey: string | null | undefined,
  theme: ThemeName = 'light'
): ProviderIcon | null {
  const normalized = normalizeProviderIconKey(providerKey)
  const def = PROVIDER_ICONS[normalized]
  if (!def) return null
  return {
    src: resolveIconSrc(def, theme),
    mode: def.mode,
    tint: def.tint
  }
}

export function getProviderIconPath(
  providerKey: string | null | undefined,
  theme: ThemeName = 'light'
): string | null {
  return getProviderIcon(providerKey, theme)?.src || null
}

export function getProviderBrandColor(providerKey: string | null | undefined): string {
  const normalized = normalizeProviderIconKey(providerKey)
  return PROVIDER_BRAND_COLORS[normalized] || '#1f1d16'
}
